/**
 * Canonical id → name lookup for the Commonwealth of Dominica Master
 * Corridors. Mirrors `src/constants/corridors.ts` on the mobile side
 * but lives here so the admin doesn't have to import from React
 * Native source. The set is stable — adding a corridor is a
 * deliberate engineering + government coordination act, not a
 * casual config edit — so keeping a small local table is far
 * cheaper than firing an extra Supabase RPC on every render of the
 * admin Drivers page.
 *
 * Keep this list in sync if a new corridor is added to
 * `src/constants/corridors.ts`. Unknown ids fall back to a Title
 * Case rendering of the id itself (`portsmouth-north` →
 * "Portsmouth North") so a never-before-seen corridor still reads
 * cleanly without breaking the page.
 */

const CORRIDOR_NAMES: Record<string, string> = {
  'west-coast': 'West Coast',
  'south-coast': 'South Coast',
  southeast: 'Southeast',
  'east-coast': 'East Coast',
  northeast: 'Northeast',
  'north-coast': 'North Coast',
  'portsmouth-north': 'Portsmouth North',
  'imperial-road': 'Imperial Road',
  'airport-shuttle': 'Airport Shuttle',
};

/**
 * Return a human-friendly corridor name for a given corridor id.
 * Returns null when the input is null/undefined so callers can
 * decide what to render in the empty case (typically "—").
 *
 * Unknown ids are converted on the fly: `some-new-corridor` →
 * "Some New Corridor". That keeps the admin readable even when the
 * mobile side ships a new corridor before this file is updated.
 */
export function getCorridorName(
  id: string | null | undefined,
): string | null {
  if (!id) return null;
  const known = CORRIDOR_NAMES[id];
  if (known) return known;
  return id
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
